import { copyDayData } from "./departmentWeeks";
import type { HospitalData } from "./models";

type ApplyShiftJobLocalParams = {
	shiftId: string;
	nurseId: number;
	newJobId: string;
	excludeFromPpd: boolean;
	/** Target the actual (`nursesActual`) list when true, the scheduled (`nurses`) list otherwise. */
	isActual: boolean;
};

/**
 * Optimistically applies a job reassignment to a single shift in the in-memory
 * hospital data, returning a new `HospitalData` (structurally shared where possible)
 * so consumers re-render without a refetch.
 *
 * Shared between the scheduled-shift job edit (StaffTable day view) and the
 * actual-shift job edit (ProfileV2 weekly grid) — both mutate the same
 * `weeks[...].days.nurses(Actual)` structure, so the update logic lives here once.
 * `jobName` is resolved from the nurse's own `jobs` list so the card label updates
 * in lockstep with the id.
 */
export function applyShiftJobLocal(data: HospitalData, { shiftId, nurseId, newJobId, excludeFromPpd, isActual }: ApplyShiftJobLocalParams): HospitalData {
	const nurse = data.nurses.find((n) => n.id === nurseId);
	const resolvedJobName = nurse?.jobs?.find((j) => j.id === newJobId)?.displayName;

	for (let weekIndex = 0; weekIndex < data.weeks.length; weekIndex++) {
		const week = data.weeks[weekIndex];
		for (let shiftIndex = 0; shiftIndex < week.weeklyShifts.length; shiftIndex++) {
			const days = week.weeklyShifts[shiftIndex].days;
			for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
				const nurseList = isActual ? days[dayIndex].nursesActual : days[dayIndex].nurses;
				if (!nurseList?.some((n) => n.id === shiftId)) continue;

				const { day, newData } = copyDayData(data, weekIndex, shiftIndex, dayIndex);
				if (isActual) {
					if (day.nursesActual) {
						day.nursesActual = day.nursesActual.map((n) => (n.id === shiftId ? { ...n, jobId: newJobId, jobName: resolvedJobName, excludeFromPpd } : n));
					}
				} else {
					day.nurses = day.nurses.map((n) => (n.id === shiftId ? { ...n, jobId: newJobId, jobName: resolvedJobName, excludeFromPpd } : n));
				}
				return newData;
			}
		}
	}
	return data;
}
